import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./components/LoginPage";
import MainFormContainer from "./components/MainFormContainer";
import UserMaintenance from "./components/UserMaintenance";
import EmailAttachment from "./components/EmailAttachment";
import Overview from "./components/Overview";
import HeaderBar from "./components/HeaderBar";
import MassUploadManager from "./components/MassUploadManager";
import CaptureUploadImage from "./components/CaptureUploadImage";
import DetailView from "./components/DetailView";
import Reports from "./components/ReportsDashboard";
import Migrate from "./components/Migrate";
import OPTMerge from "./components/OPTMerge";
import DeleteRequest from "./components/DeleteRequest";
import DeleteHeads from "./components/DeleteHeads"
import { BusyProvider } from "./BusyContext";
import localforage from 'localforage';

const CACHE_DURATION = 1000 * 60 * 60 * 24;

const App = () => {
  const [userRole, setUserRole] = useState(localStorage.getItem("userRole"));
  const [cityList, setCityList] = useState([]); // Add state for cityList

  const handleLogout = () => {
    localStorage.removeItem("userRole");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userId");
    localStorage.removeItem("userFirstName");
    localStorage.removeItem("userLastName");
    localStorage.removeItem("municipalCode");

    window.location.href = '/';
  };

  useEffect(() => {
    const userRole = parseInt(localStorage.getItem("userRole"));
    const municipalCode = localStorage.getItem("municipalCode");
    const barangayCode = localStorage.getItem("barangayCode");
    const provincialCode = localStorage.getItem("provincialCode");

    // Define what it means to be logged in
    const isLoggedIn = localStorage.getItem("userId") !== null;

    // If user is not logged in → do nothing
    if (!isLoggedIn) return;

    // Only validate if user is logged in
    if (
      (userRole === 2 && !provincialCode) ||
      (userRole === 3 && !municipalCode) ||
      ((userRole === 4 || userRole === 5) && !barangayCode)
    ) {
      alert(`Error: missing assigned code`);
      handleLogout();
    }
  }, []);

  useEffect(() => {
    // Placeholder for fetching cityList
    // Example:
    // const fetchCityList = async () => {
    //   try {
    //     const response = await fetch('/api/cities'); // Replace with your actual API endpoint
    //     const data = await response.json();
    //     setCityList(data);
    //   } catch (error) {
    //     console.error("Failed to fetch city list:", error);
    //     // Handle error, maybe set a default or show a message
    //   }
    // };
    // fetchCityList();
    // For now, using a static example if you have it, or leave as empty array
    // setCityList([...your predefined city list...]);
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "userRole") {
        setUserRole(localStorage.getItem("userRole"));
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    // Dynamically set the favicon
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href = process.env.REACT_APP_TAB_LOGO;
    }
  }, []);
  let isAuthenticated = false;
  const userBarangayCode = parseInt(localStorage.getItem("barangayCode"), 10);
  
  isAuthenticated = Boolean(userRole);
  if(userRole == 1 && userBarangayCode !== 9999){
    isAuthenticated = false;
  }
  console.log("Authenticated: ", isAuthenticated, userRole, userBarangayCode);  

  const fetchProvinces = async () => {
    const cacheProvinceKey = 'location_province';

    try {
      const cached = await localforage.getItem(cacheProvinceKey);
      const isExpired = cached && Date.now() - cached.timestamp > CACHE_DURATION;

      if (cached && !isExpired) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/locations/api/provinces`, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });
      if (!response.ok) throw new Error("Error fetching provinces.");
      const data = await response.json();

      await localforage.setItem(cacheProvinceKey, {data, timestamp: Date.now()});
    } catch {
      const cached = await localforage.getItem(cacheProvinceKey);
      if (cached) {
        console.log("Warning: Using expired cache for list of provinces");
      }
    }
  }

  const fetchMunicipalities = async () => {
    const cacheMunicipalityKey = 'location_municipality';

    try {
      const cached = await localforage.getItem(cacheMunicipalityKey);
      const isExpired = cached && Date.now() - cached.timestamp > CACHE_DURATION;

      if (cached && !isExpired) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/locations/api/municipalities`, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });
      if (!response.ok) throw new Error("Error fetching municipalities.");
      const data = await response.json();


      // grouping transformation based on province_id
      const grouped = Object.values(
        data.reduce((acc, item) => {
          const { province_id } = item;
          if (!acc[province_id]) {
            acc[province_id] = {province_id: province_id, municipalities: [] };
          }
          acc[province_id].municipalities.push(item);
          return acc;
        }, {})
      )

      await localforage.setItem(cacheMunicipalityKey, {data: grouped, timestamp: Date.now()});
    } catch {
      const cached = await localforage.getItem(cacheMunicipalityKey);
      if (cached) {
        console.log("Warning: Using expired cache for list of municipalities");
      }
    }
  }

  const fetchBarangays = async () => {
    const cacheBarangayKey = 'location_barangay';

    try {
      const cached = await localforage.getItem(cacheBarangayKey);
      const isExpired = cached && Date.now() - cached.timestamp > CACHE_DURATION;

      if (cached && !isExpired) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/locations/api/barangays`, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY },
      });
      if (!response.ok) throw new Error("Error fetching barangays.");
      const data = await response.json();

      // grouping transformation based on municipality_id
      const grouped = Object.values(
        data.reduce((acc, item) => {
          const { municipality_id } = item;
          if (!acc[municipality_id]) {
            acc[municipality_id] = {municipality_id: municipality_id, barangays: [] };
          }
          acc[municipality_id].barangays.push(item);
          return acc;
        }, {})
      )

      await localforage.setItem(cacheBarangayKey, {data: grouped, timestamp: Date.now()});
    } catch {
      const cached = await localforage.getItem(cacheBarangayKey);
      if (cached) {
        console.log("Warning: Using expired cache for list of barangays");
      }
    }
  }

  // fetch and cache location data
  useEffect(() => {
    fetchProvinces();
    fetchMunicipalities();
    fetchBarangays();
  }, [])

  return (
    <BusyProvider>
      <Router>
        <HeaderBar />
        <div style={{ marginTop: "4rem", padding: "1rem" }}>
          <Routes>
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to="/main" replace /> : <LoginPage setUserRole={setUserRole} />}
            />
            <Route
              path="/main"
              element={isAuthenticated ? <MainFormContainer userRole={userRole} cityList={cityList} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/overview"
              element={isAuthenticated ? <Overview /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/detail/:headId"
              element={isAuthenticated ? <DetailView cityList={cityList} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/mass-upload"
              element={isAuthenticated ? <MassUploadManager /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/user-maintenance"
              element={isAuthenticated ? <UserMaintenance /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/attach-email/:adminMode/:barangayId"
              element={isAuthenticated ? <EmailAttachment /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/manual-entry"
              element={isAuthenticated ? <MainFormContainer userRole={userRole} cityList={cityList} /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/capture-upload-image"
              element={isAuthenticated ? <CaptureUploadImage /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/migrate"
              element={isAuthenticated ? <Migrate /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/reports"
              element={isAuthenticated ? <Reports /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/opt-merge"
              element={isAuthenticated ? <OPTMerge /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/delete-request"
              element={isAuthenticated ? <DeleteRequest /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/delete-heads"
              element={isAuthenticated ? <DeleteHeads /> : <Navigate to="/login" replace />}
            />
            <Route path="*" element={<Navigate to={isAuthenticated ? "/main" : "/login"} replace />} />
          </Routes>
        </div>
      </Router>
    </BusyProvider>
  );
};

export default App;